'use client';

import { useTranslation } from '@/lib/i18n';
import {
  CryptoRelatedMarketEvent,
  CryptoRelatedMarketNumber,
  useCryptoRelatedMarket,
} from '@/hooks/use-crypto-related-market';
import ListWithSkeleton from '../ui/ListWithSkeleton';
import { MarketCard, type MarketCardMarket } from '../cards/market';

interface CryptoReleatedMarketProps {
  symbol: string;
}

interface CryptoRelatedMarketCardItem {
  id: string;
  href: string;
  market: MarketCardMarket;
}

export default function CryptoReleatedMarket(props: CryptoReleatedMarketProps) {
  const { symbol } = props;
  const { data, isLoading } = useCryptoRelatedMarket(symbol);
  const { t } = useTranslation();
  const marketList = data?.markets.map(toMarketCardData) || [];

  return (
    <div className="@container">
      <h2 className="py-5 text-lg  @max-xl:grid-cols-1 font-medium">{t('Related Markets')}</h2>
      <ListWithSkeleton
        loading={isLoading}
        list={marketList}
        wrapperClassName="grid grid-cols-2 gap-3 @max-xl:grid-cols-1"
        itemRender={(item) => (
          <MarketCard key={item.id} marketEventId={item.id} market={item.market} href={item.href} />
        )}
      />
    </div>
  );
}

function toMarketCardData(event: CryptoRelatedMarketEvent): CryptoRelatedMarketCardItem {
  const firstMarket = event.markets[0];
  const marketEventId = event.event_id || event.id;
  const eventSlug = event.slug || marketEventId;
  const eventTitle = event.title || firstMarket?.market_question || '';
  const poster =
    event.image || event.icon || firstMarket?.market_icon || firstMarket?.market_image || '';
  const yesPrice = toNumber(firstMarket?.outcome_yes_price);
  const change24h = toNumber(firstMarket?.price_change_24h);
  const totalVolume = toNumber(event.volume_24hr);
  const groupItemTitle =
    firstMarket?.group_item_title || firstMarket?.outcome_yes?.toUpperCase() || 'YES';

  return {
    id: marketEventId,
    href: `/predic/market?id=${eventSlug}`,
    market: {
      marketEventId,
      imageSrc: poster,
      imageAlt: eventTitle,
      title: eventTitle,
      outcome: groupItemTitle,
      probability: yesPrice * 100,
      change: change24h * 100,
      changePeriod: '(24h)',
      volume: totalVolume,
      category: event.time_category || '',
    },
  };
}

function toNumber(value?: CryptoRelatedMarketNumber) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
}
